import { callFunction } from "./functions-client";

// Admin Attack Dashboard client (security fn). Staff-only; every action is re-authorized
// server-side against the admin_security permission.
export type AbuseBand = "clean" | "watch" | "abusive" | "critical";

export interface AbusiveAccount {
  id: string; email: string | null; status: string;
  suspended_at: string | null; suspended_reason: string | null;
  security_events: number; open_reports: number; upheld_reports: number;
  risk_band: string | null; score: number; band: AbuseBand;
}
export interface SecurityEvent {
  id: number; account_id: string | null; kind: string; severity: number;
  ip: string | null; path: string | null; created_at: string;
}
export interface RateHotspot { key: string; count: number; window_start: string }

export interface Dashboard {
  top_accounts: AbusiveAccount[];
  event_counts: Record<string, number>;
  recent_events: SecurityEvent[];
  rate_hotspots: RateHotspot[];
  suspended_count: number;
}

export interface AccountDetail {
  account: { id: string; email: string | null; status: string; identity_verified: boolean; suspended_at: string | null; suspended_reason: string | null; suspended_by: string | null; created_at: string };
  signals: { security_events: number; weighted_severity: number; open_reports: number; upheld_reports: number; risk_band: string | null; status: string };
  score: number; band: AbuseBand;
  security_events: (SecurityEvent & { metadata?: Record<string, unknown> })[];
  risk_events: { id: number; kind: string; score: number | null; band: string | null; created_at: string }[];
  reports: { id: string; reason: string; status: string; resolution: string | null; created_at: string }[];
}

export const securityDashboard = () =>
  callFunction<Dashboard>("security/dashboard", { method: "GET" });

export const securityAccount = (id: string) =>
  callFunction<AccountDetail>(`security/account?id=${encodeURIComponent(id)}`, { method: "GET" });

export const securityEvents = (opts: { kind?: string; since?: string; limit?: number } = {}) => {
  const p = new URLSearchParams();
  if (opts.kind) p.set("kind", opts.kind);
  if (opts.since) p.set("since", opts.since);
  if (opts.limit) p.set("limit", String(opts.limit));
  const qs = p.toString();
  return callFunction<{ events: SecurityEvent[] }>(`security/events${qs ? `?${qs}` : ""}`, { method: "GET" });
};

export const suspendAccount = (account_id: string, reason: string) =>
  callFunction<{ ok: boolean }>("security/suspend", { body: { account_id, reason } });

export const terminateAccount = (account_id: string, reason: string) =>
  callFunction<{ ok: boolean }>("security/terminate", { body: { account_id, reason } });

export const reinstateAccount = (account_id: string) =>
  callFunction<{ ok: boolean }>("security/reinstate", { body: { account_id } });

export const ABUSE_BAND_TONE: Record<AbuseBand, string> = {
  clean: "text-muted", watch: "text-[#c9a227]", abusive: "text-danger", critical: "text-danger",
};
